import { Arg, Args, Ctx, Directive, FieldResolver, Query, Resolver, Root } from 'type-graphql'

import { User } from '@things-factory/auth-base'
import { Domain, getQueryBuilderFromListParams, getRepository, ListParam } from '@things-factory/shell'
import { ActivityInstance } from '@things-factory/worklist'

import { DataSetHistory } from '../data-set-history/data-set-history.js'
import { DataItem } from '../data-set/data-item-type.js'
import { DataSet } from '../data-set/data-set.js'
import { DataSample } from '../data-sample/data-sample.js'
import { DataOoc } from './data-ooc.js'
import { DataOocList } from './data-ooc-type.js'

@Resolver(DataOoc)
export class DataOocQuery {
  @Directive('@privilege(category: "data-ooc", privilege: "query", domainOwnerGranted: true)')
  @Query(returns => DataOoc, {
    description:
      'Retrieves a single out-of-control (OOC) record by its unique identifier. This query fetches detailed information about an OOC event including its status, corrective actions, review process, and associated data sample.'
  })
  async dataOoc(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<DataOoc> {
    const { domain } = context.state

    return await getRepository(DataOoc).findOne({
      where: { domain: { id: domain.id }, id }
    })
  }

  @Directive('@privilege(category: "data-ooc", privilege: "query", domainOwnerGranted: true)')
  @Query(returns => DataOocList, {
    description:
      'Retrieves multiple out-of-control (OOC) records with pagination and filtering support. This query allows searching OOC events by name and description, and is used for monitoring and managing quality control issues across all datasets.'
  })
  async dataOocs(@Args(type => ListParam) params: ListParam, @Ctx() context: ResolverContext): Promise<DataOocList> {
    const { domain } = context.state

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(DataOoc),
      params,
      domain,
      searchables: ['name', 'description']
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @FieldResolver(type => [DataItem], {
    description:
      'Retrieves the data items (field definitions) associated with this OOC record. These items define the structure and validation rules for the OOC data, including field names, types, and constraints.'
  })
  async dataItems(@Root() dataOoc: DataOoc): Promise<DataItem[]> {
    const dataSetHistory: DataSetHistory =
      dataOoc.dataSetId &&
      (await getRepository(DataSetHistory).findOne({
        where: {
          originalId: dataOoc.dataSetId,
          version: dataOoc.dataSetVersion
        }
      }))
    return dataSetHistory?.dataItems.filter(item => item.active) || []
  }

  @FieldResolver(type => DataSample, {
    description:
      'Retrieves the original data sample that triggered this OOC event. This field resolver provides access to the source data that was flagged as out of control or out of specification.'
  })
  async dataSample(@Root() dataOoc: DataOoc): Promise<DataSample> {
    return dataOoc.dataSampleId && (await getRepository(DataSample).findOneBy({ id: dataOoc.dataSampleId }))
  }

  @FieldResolver(type => DataSet, {
    description:
      'Retrieves the dataset that this OOC event belongs to. This field resolver provides access to the dataset configuration and settings associated with the OOC event.'
  })
  async dataSet(@Root() dataOoc: DataOoc): Promise<DataSet> {
    return dataOoc.dataSetId && (await getRepository(DataSet).findOneBy({ id: dataOoc.dataSetId }))
  }

  @FieldResolver(type => ActivityInstance, {
    description:
      'Retrieves the review activity instance associated with this OOC event. This field resolver provides access to the workflow process for reviewing and approving the OOC event.'
  })
  async reviewActivityInstance(@Root() dataOoc: DataOoc): Promise<ActivityInstance> {
    return (
      dataOoc.reviewActivityInstanceId &&
      (await getRepository(ActivityInstance).findOneBy({ id: dataOoc.reviewActivityInstanceId }))
    )
  }

  @FieldResolver(type => ActivityInstance, {
    description:
      'Retrieves the resolution activity instance associated with this OOC event. This field resolver provides access to the workflow process for implementing corrective actions and resolving the OOC issue.'
  })
  async resolveActivityInstance(@Root() dataOoc: DataOoc): Promise<ActivityInstance> {
    return (
      dataOoc.resolveActivityInstanceId &&
      (await getRepository(ActivityInstance).findOneBy({ id: dataOoc.resolveActivityInstanceId }))
    )
  }

  @FieldResolver(type => User, {
    description:
      'Retrieves the user who implemented the corrective action for this OOC event. This field resolver identifies the person responsible for resolving the out-of-control situation.'
  })
  async corrector(@Root() dataOoc: DataOoc): Promise<User> {
    return dataOoc.correctorId && (await getRepository(User).findOneBy({ id: dataOoc.correctorId }))
  }

  @FieldResolver(type => User, {
    description:
      'Retrieves the user who reviewed this OOC event. This field resolver identifies the person responsible for reviewing and approving the out-of-control situation.'
  })
  async reviewer(@Root() dataOoc: DataOoc): Promise<User> {
    return dataOoc.reviewerId && (await getRepository(User).findOneBy({ id: dataOoc.reviewerId }))
  }

  @FieldResolver(type => Domain)
  async domain(@Root() dataOoc: DataOoc): Promise<Domain> {
    return await getRepository(Domain).findOneBy({ id: dataOoc.domainId })
  }

  @FieldResolver(type => User)
  async updater(@Root() dataOoc: DataOoc): Promise<User> {
    return dataOoc.updaterId && (await getRepository(User).findOneBy({ id: dataOoc.updaterId }))
  }

  @FieldResolver(type => User)
  async creator(@Root() dataOoc: DataOoc): Promise<User> {
    return dataOoc.creatorId && (await getRepository(User).findOneBy({ id: dataOoc.creatorId }))
  }
}
